import React, { useState } from 'react';
import {
    Box, Chip, Typography, Tooltip, Paper, useTheme, useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

interface LegendItem {
    value: string;
    type: string;
    id: string;
    color: string;
}

interface CustomLegendProps {
    payload: LegendItem[];
    onToggleMetric?: (metricId: string) => void;
    onToggleAllMetrics?: (hideAll: boolean) => void;
    hiddenMetrics?: Set<string>;
    maxItemsToShow?: number;
}

export const CustomLegend: React.FC<CustomLegendProps> = ({
    payload,
    onToggleMetric,
    onToggleAllMetrics,
    hiddenMetrics = new Set(),
    maxItemsToShow = 6
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [showAll, setShowAll] = useState(false);

    if (!payload || payload.length === 0) {
        return null;
    }

    const totalItems = payload.length;
    const shouldShowExpandButton = totalItems > maxItemsToShow;
    const itemsToDisplay = showAll ? payload : payload.slice(0, maxItemsToShow);

    const handleToggleMetric = (metricId: string) => {
        if (onToggleMetric) {
            onToggleMetric(metricId);
        }
    };

    const handleToggleAll = () => {
        if (onToggleAllMetrics) {
            const shouldHideAll = hiddenMetrics.size < totalItems;
            onToggleAllMetrics(shouldHideAll);
        }
    };

    const renderMetricChip = (item: LegendItem, isCompact: boolean = false) => {
        const isHidden = hiddenMetrics.has(item.id);

        return (
            <Tooltip
                key={item.id}
                title={
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {item.value}
                        </Typography>
                        <Typography variant="caption">
                            {isHidden ? "Click to show this metric" : "Click to hide this metric"}
                        </Typography>
                    </Box>
                }
            >
                <Chip
                    label={item.value}
                    onClick={() => handleToggleMetric(item.id)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleToggleMetric(item.id);
                        }
                    }}
                    tabIndex={0}
                    icon={
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: item.color,
                                border: '1px solid rgba(255,255,255,0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {isHidden && (
                                <VisibilityOffIcon
                                    sx={{
                                        fontSize: 8,
                                        color: 'white',
                                        filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.8))'
                                    }}
                                />
                            )}
                        </Box>
                    }
                    variant={isHidden ? "outlined" : "filled"}
                    size={isMobile || isCompact ? "small" : "medium"}
                    sx={{
                        margin: 0.5,
                        backgroundColor: isHidden ? 'transparent' : `${item.color}20`,
                        borderColor: item.color,
                        color: isHidden ? 'text.secondary' : 'text.primary',
                        opacity: isHidden ? 0.6 : 1,
                        transition: 'all 0.2s ease',
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: isHidden ? `${item.color}10` : `${item.color}30`,
                            transform: 'scale(1.02)',
                        },
                        '&:focus': {
                            outline: '2px solid',
                            outlineColor: 'primary.main',
                            outlineOffset: '2px',
                        },
                        '& .MuiChip-label': {
                            fontSize: isCompact ? '0.75rem' : '0.875rem',
                            fontWeight: isHidden ? 400 : 500
                        }
                    }}
                />
            </Tooltip>
        );
    };

    const renderSimpleLegend = () => (
        <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center'
        }}>
            {itemsToDisplay.map(item => renderMetricChip(item, true))}
        </Box>
    );

    return (
        <Paper
            elevation={1}
            sx={{
                p: 1, mt: 1, 
                backgroundColor: 'background.paper',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.2s ease',
                position: 'relative',
                overflow: 'visible',
                '&:hover': {
                    elevation: 2,
                    borderColor: 'primary.main',
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'text.secondary'
                }}>
                    Metrics ({totalItems}{hiddenMetrics.size > 0 ? `, ${hiddenMetrics.size} hidden` : ''})
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {onToggleMetric && onToggleAllMetrics && (
                        <Chip
                            label={hiddenMetrics.size < totalItems ? "Hide All" : "Show All"}
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={handleToggleAll}
                            sx={{
                                cursor: 'pointer',
                                height: '22px',
                                fontSize: '0.7rem',
                                '&:hover': {
                                    backgroundColor: 'primary.main',
                                    color: 'primary.contrastText'
                                }
                            }}
                        />
                    )}
                </Box>
            </Box>
            <Box>
                {renderSimpleLegend()}
                {shouldShowExpandButton && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                        <Chip
                            label={showAll ? `Show Less (${maxItemsToShow})` : `Show All (${totalItems})`}
                            variant="outlined"
                            size="small"
                            onClick={() => setShowAll(!showAll)}
                            icon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: 'action.hover'
                                }
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default CustomLegend;
